// strategySearch.js — Paper 전략 자유 텍스트 검색 매칭 (프론트 표시 계층).
//
// 운영 그리드에서 "전략별로 찾아 보기"를 위한 클라이언트 사이드 검색. 백엔드
// 무변경 원칙(strategyGrouping/strategyFamily 와 동일)에 따라 이미 받아온 목록을
// id·한글 이름·요약·paradigm·패밀리 라벨에 대해 필터링한다.
//
// 매칭 규칙:
//   - 공백으로 토큰 분리 후 모든 토큰이 포함돼야 매칭(AND). "liq btc" → 청산 +
//     BTC 둘 다 들어간 항목만.
//   - 대소문자 무시.
//   - 그룹(패밀리)은 그룹 자신의 이름/패밀리 + 모든 멤버 텍스트를 합친 건초더미로
//     검사한다 → 멤버 코인명(예: "doge")으로도 그룹이 잡힌다.

import { getStrategyMeta } from "./strategyMeta";
import { readStrategyFields } from "../components/paper/PaperStrategyCard";
import { familyBadge } from "./strategyFamily";

/** 검색어 정규화 — trim + 소문자. 문자열이 아니면 빈 문자열. */
export function normalizeQuery(q) {
    return typeof q === "string" ? q.trim().toLowerCase() : "";
}

/** 단일 전략 1개의 검색 대상 텍스트(소문자)를 만든다. */
function singleHaystack(s) {
    const f = readStrategyFields(s);
    const meta = getStrategyMeta(f.id);
    const fam = familyBadge(s);
    return [f.id, meta?.name, meta?.summary, meta?.paradigm, fam?.label]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
}

/** 그룹(패밀리)의 검색 대상 텍스트 — 그룹 메타 + 패밀리 + 모든 멤버 텍스트. */
function groupHaystack(group) {
    const members = Array.isArray(group.members) ? group.members : [];
    const firstMeta = members.length
        ? getStrategyMeta(readStrategyFields(members[0]).id)
        : null;
    const fam = familyBadge(group);
    const memberText = members.map((m) => singleHaystack(m)).join(" ");
    return [
        group.baseId,
        readStrategyFields(group).id,
        firstMeta?.name,
        firstMeta?.summary,
        firstMeta?.paradigm,
        fam?.label,
        memberText,
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
}

/**
 * 전략(단일/그룹) 1개가 검색어에 매칭되는지. 빈 검색어면 항상 true.
 * @param {object} s 전략 객체 또는 groupStrategies 그룹
 * @param {string} query
 */
export function matchesQuery(s, query) {
    const tokens = normalizeQuery(query).split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return true;
    const hay = s?.isGroup ? groupHaystack(s) : singleHaystack(s);
    return tokens.every((t) => hay.includes(t));
}

/**
 * 전략 배열을 검색어로 필터링. 빈 검색어면 원본 그대로 반환.
 * @param {Array<object>} strategies
 * @param {string} query
 * @returns {Array<object>}
 */
export function searchStrategies(strategies, query) {
    if (!normalizeQuery(query)) return strategies;
    if (!Array.isArray(strategies)) return [];
    return strategies.filter((s) => matchesQuery(s, query));
}
